import { CkMinterClient } from '../clients/CkMintClient'
import OdinBridgeClient from '../clients/OdinBridgeClient'
import { OneSecBridgeClient } from '../clients/OneSecBridgeClient'
import { PERP_FUNDING_SOURCE_IDS } from '../constants/business'
import { USDC_ARB_ID, USDC_HYPERCORE_PERP_ID, USDC_HYPEREVM_ID } from '../constants/tokenIds'
import { Chain, EVMChain, PlugToken } from '../interfaces/common'

const getUniqueTokens = (tokens: PlugToken[]) => {
  const uniqueIds = new Set()

  return tokens.filter((token) => {
    if (uniqueIds.has(token.id)) {
      return false
    } else {
      uniqueIds.add(token.id)
      return true
    }
  })
}

const supportsStealthEx = ({ additionalData }: PlugToken) =>
  additionalData.stealthex_symbol && additionalData.stealthex_network

export const filterDestinationTokens = (
  token: PlugToken | undefined,
  allTokens: PlugToken[],
  evmChains: EVMChain[]
) => {
  if (!token?.chain) return allTokens

  const evmChainIds = Object.fromEntries(evmChains.map((c) => [c.chainId, true]))
  let result: PlugToken[] = []

  if (token.chain === Chain.SOL || token.evmChain) {
    result = allTokens.filter((t) => t.chain === Chain.SOL || (t.chain && evmChainIds[t.chain]))
  } else if (token.chain === Chain.ICP) {
    result = allTokens.filter((t) => t.chain === Chain.ICP)
  } else if (token.chain === Chain.ODIN) {
    result = allTokens.filter((t) => t.chain === Chain.ODIN)
  } else if (token.chain === Chain.HYPERCORE) {
    // The opposite HyperCore account (perp <-> spot) plus the linked HyperEVM token
    result = allTokens.filter(
      (t) =>
        (t.chain === Chain.HYPERCORE &&
          t.additionalData.hypercore_account !== token.additionalData.hypercore_account) ||
        t.id === USDC_HYPEREVM_ID
    )
  }

  // HyperCore perp -> USDC on Arbitrum (withdraw off HyperLiquid)
  if (token.id === USDC_HYPERCORE_PERP_ID) {
    const usdcArb = allTokens.find((t) => t.id === USDC_ARB_ID)
    if (usdcArb) result = [...result, usdcArb]
  }

  // HyperEVM token -> its linked HyperCore spot account
  if (token.id === USDC_HYPEREVM_ID) {
    result = [
      ...result,
      ...allTokens.filter(
        (t) =>
          t.chain === Chain.HYPERCORE &&
          t.additionalData.hypercore_account === 'spot' &&
          t.symbol === token.symbol
      ),
    ]
  }

  // Arbitrum-bridge sources can fund the HyperCore perp account
  if (PERP_FUNDING_SOURCE_IDS.includes(token.id)) {
    const perpToken = allTokens.find((t) => t.id === USDC_HYPERCORE_PERP_ID)
    if (perpToken) result = [...result, perpToken]
  }

  if (supportsStealthEx(token)) {
    result = [...result, ...allTokens.filter(supportsStealthEx)]
  }

  ;[
    ...OdinBridgeClient.supportedTokens,
    ...OneSecBridgeClient.supportedTokens,
    ...CkMinterClient.supportedTokens,
  ].forEach((tokenIds) => {
    if (tokenIds.includes(token.id)) {
      const tokens = tokenIds.map((id) => allTokens.find((t) => t.id === id)).filter((t) => !!t)
      result = [...result, ...tokens]
    }
  })

  return getUniqueTokens(result).filter((t) => t.id !== token.id)
}
